/**
 * Reading the study-level monadic lane fact in the browser.
 *
 * A twin-monadic study stores each lane answer as
 * `survey_data[question][assetId]`, a shape that is byte-identical to a
 * SurveyJS matrix answer. **Scope can never be inferred from the answer.** It
 * comes from `CanonicalAnalysisOutput.monadicLaneScope`, which the server
 * derived once from the engine's own `partition_monadic` (plan decision 1 and
 * 12). Nothing here re-derives lane scope from `assetBinding.mode`, from the
 * compiled survey, or from `isPlainObject(answer)`. The one binding reader,
 * `declaresPerAssetBinding`, answers a different question (is a NON-lane
 * question asset-keyed), as Python's `_metric_is_asset_keyed` does.
 *
 * There is deliberately **no TypeScript port of the bucket logic** (0.B6). The
 * browser splits answers by asset key and reuses `aggregateSimple`, the
 * aggregator it already had.
 */

import { aggregateSimple, getQuestionValue, isDontKnowValue } from './aggregation'

/**
 * Mirror of `MonadicLaneScope` in shared-types, typed loosely here because
 * deck-ui receives whatever the analysis blob carried, including blobs written
 * before the field existed.
 */
export interface MonadicLaneScopeFact {
  laneQuestionNames?: Array<string> | null
  laneAssetIds?: Array<string> | null
}

export interface LaneMergedAssetSeries {
  assetId: string
  label: string
  /** One share per entry of `optionValues`, 0-100, unique respondents. */
  values: Array<number>
  /** Respondents in this lane with at least one usable selection. */
  base: number
}

export interface LaneMergedChoiceShares {
  /** Option values, in AUTHORED order. */
  optionValues: Array<string>
  /** Display labels for `optionValues`, same length and order. */
  labels: Array<string>
  series: Array<LaneMergedAssetSeries>
}

function isAssetMap(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The lane assets this question was answered in, or an empty array.
 *
 * Both halves of the fact are required: a question named in
 * `laneQuestionNames` with no `laneAssetIds` ran no lanes, and asset ids
 * without the question name say nothing about this question.
 */
export function laneAssetIdsForQuestion(
  question: { name?: string } | null | undefined,
  scope: MonadicLaneScopeFact | null | undefined,
): Array<string> {
  const name = question?.name
  if (!name || !scope) return []
  const names = scope.laneQuestionNames || []
  const assetIds = scope.laneAssetIds || []
  if (assetIds.length === 0 || !names.includes(name)) return []
  return assetIds
}

/**
 * One response row seen from one lane, or `null` when it holds nothing there.
 *
 * Shallow-copies only the container that actually holds the answer and only
 * that one key inside it, so unfolding N assets never deep-copies a response
 * and every other question keeps its original reference. Mirrors
 * `canonical_analysis/monadic_scope.py::unfold_row_for_asset`.
 *
 * A bare scalar returns `null`: a value with no asset key belongs to no lane,
 * and folding it into one would be a fabricated attribution.
 */
export function unfoldResponseForAsset(
  response: any,
  questionName: string,
  assetId: string,
): any | null {
  if (!response || typeof response !== 'object') return null
  for (const containerKey of ['survey_data', 'answers', 'response']) {
    const container = response[containerKey]
    if (!isAssetMap(container) || !(questionName in container)) continue
    const answer = container[questionName]
    if (!isAssetMap(answer) || !(assetId in answer)) return null
    return { ...response, [containerKey]: { ...container, [questionName]: answer[assetId] } }
  }
  if (questionName in response) {
    const answer = response[questionName]
    if (!isAssetMap(answer) || !(assetId in answer)) return null
    return { ...response, [questionName]: answer[assetId] }
  }
  return null
}

export function unfoldResponsesForAsset(
  responses: Array<any>,
  questionName: string,
  assetId: string,
): Array<any> {
  const out: Array<any> = []
  for (const response of responses || []) {
    const unfolded = unfoldResponseForAsset(response, questionName, assetId)
    if (unfolded) out.push(unfolded)
  }
  return out
}

export interface UnfoldedLane {
  assetId: string
  responses: Array<any>
}

/**
 * One unfolded response set per lane asset, in the fact's order.
 *
 * Empty when the question is not in the study-level fact. A lane nobody
 * answered still appears with `responses: []`, so callers can show an empty
 * lane rather than omit it.
 *
 * This is the browser split: callers then run the aggregator they already
 * had. There is no TypeScript `partition_monadic`.
 */
export function unfoldResponsesForLane(
  question: { name?: string } | null | undefined,
  responses: Array<any>,
  scope: MonadicLaneScopeFact | null | undefined,
): Array<UnfoldedLane> {
  return laneAssetIdsForQuestion(question, scope).map((assetId) => ({
    assetId,
    responses: unfoldResponsesForAsset(responses || [], question?.name || '', assetId),
  }))
}

/**
 * Whether the compiled survey binds this question `per_asset`: asked of every
 * twin about every asset, several answers per person, lane or not.
 *
 * This is the second typed fact `canonical_analysis/runtime.py`'s
 * `_metric_is_asset_keyed` reads, and it reads it BEFORE lane membership, so a
 * `per_asset` question the lane fact does not name (a "shared" exposure zone
 * question asked per asset) is still asset-keyed. The browser follows Python
 * here. It reads the declared binding
 * (element `_canonical.assetBinding`, or the questionMap entry's mirror passed
 * as `entryBinding`), never the shape of an answer.
 */
export function declaresPerAssetBinding(
  question: { _canonical?: { assetBinding?: { mode?: unknown } | null } | null } | null | undefined,
  entryBinding?: { mode?: unknown } | null,
): boolean {
  const binding = question?._canonical?.assetBinding ?? entryBinding
  return binding?.mode === 'per_asset'
}

export function isLaneMergedQuestion(
  question: { name?: string } | null | undefined,
  scope: MonadicLaneScopeFact | null | undefined,
): boolean {
  return laneAssetIdsForQuestion(question, scope).length > 0
}

/**
 * Usable selections on one answer, applying the aggregator's own N/A rule.
 * The question travels so a declared option spelled like a sentinel ("None")
 * counts, the same as `aggregateSimple` and the canonical owner.
 */
function usableSelections(value: unknown, question: any): Array<string> {
  if (value === undefined || value === null || value === '') return []
  const list = Array.isArray(value) ? value : [value]
  return list.filter((item) => !isDontKnowValue(item, question)).map((item) => String(item))
}

/**
 * Authored option values for a choice question, in the order it was written.
 *
 * 8.5: option order is the author's, never the size order `aggregateSimple`
 * sorts by. Values seen in the data but declared nowhere are appended so no
 * answer disappears.
 */
function authoredOptionValues(question: any, seen: Array<string>): Array<string> {
  const declared: Array<string> = []
  for (const choice of question?.choices || []) {
    const value = String(choice?.value ?? choice ?? '')
    if (value && !declared.includes(value)) declared.push(value)
  }
  const ordered = [...declared]
  for (const value of seen) {
    if (!ordered.includes(value)) ordered.push(value)
  }
  return ordered
}

/**
 * Per-lane option shares for a lane-merged choice question, or `null`.
 *
 * `null` means "this question is not lane-merged" — the caller keeps its
 * ordinary single-series path. It never means "no data": a lane nobody
 * answered comes back with `base: 0` and zeroes, so the lane is visible as
 * empty rather than missing.
 *
 * The denominator is respondents in that lane with at least one usable
 * selection, which is what `_extract_multi_select_share` uses server-side. It
 * deliberately does NOT touch `SingleChoiceChart`'s pooled denominators, which
 * are their own PR (0.A2).
 */
export function laneMergedChoiceShares(
  question: any,
  responses: Array<any>,
  scope: MonadicLaneScopeFact | null | undefined,
  labelForAsset: (assetId: string) => string = (assetId) => assetId,
): LaneMergedChoiceShares | null {
  const assetIds = laneAssetIdsForQuestion(question, scope)
  if (assetIds.length === 0) return null

  const perAsset = assetIds.map((assetId) => {
    const unfolded = unfoldResponsesForAsset(responses || [], question.name, assetId)
    const base = unfolded.reduce(
      (count, response) =>
        count + (usableSelections(getQuestionValue(response, question), question).length > 0 ? 1 : 0),
      0,
    )
    const counts = new Map<string, number>()
    for (const point of aggregateSimple(question, unfolded)) {
      counts.set(String(point.rawValue ?? point.name), point.value)
    }
    return { assetId, base, counts }
  })

  const seen: Array<string> = []
  for (const { counts } of perAsset) {
    for (const value of counts.keys()) {
      if (!seen.includes(value)) seen.push(value)
    }
  }
  const optionValues = authoredOptionValues(question, seen)
  if (optionValues.length === 0) return null

  return {
    optionValues,
    labels: optionValues.map((value) => resolveOptionLabel(question, value)),
    series: perAsset.map(({ assetId, base, counts }) => ({
      assetId,
      label: labelForAsset(assetId),
      values: optionValues.map((value) =>
        base > 0 ? round6(((counts.get(value) || 0) / base) * 100) : 0,
      ),
      base,
    })),
  }
}

function round6(value: number): number {
  return Math.round(value * 1e6) / 1e6
}

function resolveOptionLabel(question: any, value: string): string {
  for (const choice of question?.choices || []) {
    if (typeof choice === 'string') {
      if (choice === value) return choice
      continue
    }
    if (String(choice?.value ?? '') === value) {
      return String(choice?.text ?? choice?.value ?? value)
    }
  }
  return value
}
